<script setup>
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import AppSidebar from './AppSidebar.vue';
import AppTopBar from './AppTopBar.vue';
import CommandPalette from '@/Components/ShopManagement/CommandPalette.vue';
import ContentSkeleton from '@/Components/ShopManagement/ContentSkeleton.vue';
import DestinationPageSkeleton from '@/Components/ShopManagement/loading/DestinationPageSkeleton.vue';
import PageLoadingBoundary from '@/Components/ShopManagement/loading/PageLoadingBoundary.vue';
import UpdateAvailableBanner from '@/Components/ShopManagement/UpdateAvailableBanner.vue';
import { SHOP_MANAGEMENT_UNSAVED_CHANGES_GROUP } from '@/Components/ShopManagement/UnsavedChangesController.js';
import { useAppVersionCheck } from '@/Composables/ShopManagement/useAppVersionCheck.js';
import { useInertiaContentSkeleton } from '@/Composables/ShopManagement/useInertiaContentSkeleton.js';
import { useNavigationScroll } from '@/Composables/ShopManagement/useNavigationScroll.js';
import { useShopManagementLayout } from '@/Composables/ShopManagement/useShopManagementLayout.js';
import { useShopManagementScope } from '@/Composables/ShopManagement/useShopManagementScope.js';
import { useShopManagementShortcuts } from '@/Composables/ShopManagement/useShopManagementShortcuts.js';
import { provideShopManagementShortcutScope } from '@/Composables/ShopManagement/useShopManagementShortcutScope.js';
import {
  createPageSectionNavigation,
  SHOP_MANAGEMENT_PAGE_SECTIONS_KEY,
} from '@/Composables/ShopManagement/usePageSectionNavigation.js';
import { resolveShopManagementNavItems } from '@/ShopManagement/navigation.js';
import { BAYHIVE_PAGE_HEADER_STATUS_INDICATORS_VISIBLE } from '@/ShopManagement/pageHeaderStatusIndicators.js';
import { timeClockClient } from '@/ShopManagement/timeClockClient.js';
import { createVendorInvoiceTermsContext } from '@/ShopManagement/vendorInvoiceTerms.js';
import SidebarBackdrop from 'primevue/sidebarbackdrop';
import SidebarLayout from 'primevue/sidebarlayout';
import SidebarMain from 'primevue/sidebarmain';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import Message from 'primevue/message';
import ScrollTop from 'primevue/scrolltop';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import { ADMIN_WORKSPACE, useAdminWorkspace } from '@/BayHiveAdmin/workspace.js';

const AuthFlowPreview = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@/Components/BayHiveAuth/AuthFlowPreview.vue'))
  : null;

const page = usePage();
provide('vendorInvoiceTerms', createVendorInvoiceTermsContext(() => page.props.vendorInvoiceTerms));
const { enabled: shortcutsEnabled } = provideShopManagementShortcutScope();
const adminWorkspace = useAdminWorkspace(page);
const isAdmin = adminWorkspace.active;
provide(ADMIN_WORKSPACE, adminWorkspace);
const shell = ref(null);
const topbarHost = ref(null);
const contentHost = ref(null);
const contentEndInset = ref(null);
const contentEndInsetOwner = ref(null);
const pagePrototypeOverride = ref(null);
const pageUnfinishedOverride = ref(null);
const sidebarId = 'shop-management-sidebar';
// Persist the desktop sidebar expand/collapse choice so a page reload keeps it
// where the user left it. The mobile overlay is transient (the resize logic
// force-closes it) so we only ever store the desktop state.
const SIDEBAR_STATE_STORAGE_KEY = 'shop-management:sidebar-open';
const readPersistedSidebarOpen = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_STATE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};
const sidebarOpen = ref(readPersistedSidebarOpen());
const commandPaletteOpen = ref(false);
const settingsVisitError = ref(null);
const pageSectionNavigation = createPageSectionNavigation();
const pageSections = pageSectionNavigation.sections;
const unsavedChangesConfirmationGroup = SHOP_MANAGEMENT_UNSAVED_CHANGES_GROUP;
const actionConfirmationGroup = 'shop-management-actions';
let pendingSettingsVisit = null;
let removeSettingsVisitStartListener = null;
let removeSettingsVisitInvalidListener = null;
let removeSettingsVisitFinishListener = null;
const showAuthPreview = computed(() => (
  import.meta.env.DEV
  && typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('auth-preview') === '1'
));

const { shopScopes, scopeContext, setScope } = useShopManagementScope(page);
const {
  breakpoints,
  isMobileShell,
  shellWidth,
  isNarrowTopbar,
  topbarMode,
  topbarHeight,
} = useShopManagementLayout({ shell, topbarHost, sidebarOpen });
const { contentLoading, destinationSkeleton, showContentSkeleton, visitKey, pageKey, presentationReady } = useInertiaContentSkeleton(page);
const { restoring: restoringNavigation } = useNavigationScroll({
  content: contentHost, page, enabled: computed(() => !isAdmin.value), navigationLoading: contentLoading,
});
const pageContentHidden = computed(() => showContentSkeleton.value || restoringNavigation.value);
const { updateAvailable, applyUpdate } = useAppVersionCheck();

const currentPage = computed(() => page.props.pages || page.props.page || {});
const currentNavKey = computed(
  () => currentPage.value.navKey || currentPage.value.key || 'dashboard',
);
const pageIsPrototype = computed(() => (
  pagePrototypeOverride.value ?? Boolean(currentPage.value.isPrototype)
));
const user = computed(() => page.props.auth?.user);
const timeClocksEnabled = computed(() => (
  page.props.showTimeClock !== false
  && timeClockClient.state.settings.enabled !== false
));
const navItems = computed(() =>
  isAdmin.value
    ? []
    : resolveShopManagementNavItems(page.props).filter(
        (item) =>
          (!item.requiresAllShopsScope || scopeContext.isAllShops) &&
          (!item.hideInAllShopsScope || !scopeContext.isAllShops) &&
          (scopeContext.isAllShops
            ? item.canAccessInAllShopsScope !== false
            : item.canAccessInShopScope !== false) &&
          (item.key !== 'time-clock' || timeClocksEnabled.value),
      ),
);
const showSidebarBackdrop = computed(() => isMobileShell.value && sidebarOpen.value);
// Fixed legacy overlays cannot participate in SidebarLayout's flow. Keep their
// viewport anchors aligned with the actual start of the visible page area.
const fixedOverlayInset = computed(() => {
  const sidebarWidth = 'var(--p-bayhive-layout-sidebar-width, 18rem)';
  const sidebarIconWidth = 'var(--p-bayhive-layout-sidebar-icon-width, 4.5rem)';

  if (isMobileShell.value) {
    return sidebarOpen.value ? sidebarWidth : '0px';
  }

  return sidebarOpen.value ? sidebarWidth : sidebarIconWidth;
});
const shellStyle = computed(() => ({
  '--p-bayhive-shell-fixed-overlay-inset': fixedOverlayInset.value,
}));
const pageIsUnfinished = computed(() => (
  pageUnfinishedOverride.value ?? scopeContext.isAllShops
));
const showUnfinishedStatus = computed(() => (
  BAYHIVE_PAGE_HEADER_STATUS_INDICATORS_VISIBLE
  && pageIsUnfinished.value
  && !pageIsPrototype.value
));
// The toolbar already owns its horizontal padding; only reserve the drawer.
const topbarStyle = computed(() => (
  Number.isFinite(contentEndInset.value) && contentEndInset.value > 0
    ? { paddingInlineEnd: `${contentEndInset.value}px` }
    : undefined
));
const contentStyle = computed(() => {
  const style = {};

  // Only content descendants need the header's measured sticky offset.
  if (topbarHeight.value > 0) {
    style['--p-bayhive-layout-topbar-height'] = `${topbarHeight.value}px`;
  }

  if (Number.isFinite(contentEndInset.value) && contentEndInset.value > 0) {
    style.paddingInlineEnd =
      `calc(var(--p-bayhive-shell-content-padding-inline) + ${contentEndInset.value}px)`;
  }

  // The status chip occupies the first row of the padded content area. Keep
  // a shared page header below it instead of pulling the header over the chip.
  if (showUnfinishedStatus.value) {
    style['--p-bayhive-page-header-margin-block-start'] = '0px';
  }

  return Object.keys(style).length > 0 ? style : undefined;
});

const setContentEndInset = (owner, width) => {
  const normalizedWidth = Number(width);
  if (!owner || !Number.isFinite(normalizedWidth) || normalizedWidth <= 0) return;
  contentEndInsetOwner.value = owner;
  contentEndInset.value = normalizedWidth;
};

const clearContentEndInset = (owner) => {
  if (contentEndInsetOwner.value !== owner) return;
  contentEndInsetOwner.value = null;
  contentEndInset.value = null;
};

const estimateShopId = computed(() => {
  if (scopeContext.isAllShops) {
    return null;
  }

  return scopeContext.selectedScope?.shopId || page.props.master_config?.shop_id || null;
});
const showEstimate = computed(() => !isAdmin.value && Boolean(estimateShopId.value));
const showTimeClock = computed(() => (
  !isAdmin.value && timeClocksEnabled.value
  && !scopeContext.isAllShops
  && Boolean(estimateShopId.value)
));
const createEstimate = (event) => {
  if (isAdmin.value) return;
  commandPaletteOpen.value = false;
  const shopId = event?.detail?.shopId || estimateShopId.value;
  if (!shopId) return;

  router.visit(route('shop-management.repair-orders.new', { shop_id: shopId }));
};

const visitPathname = (url) => {
  try {
    return new URL(String(url), window.location.origin).pathname;
  } catch {
    return String(url || '').split(/[?#]/)[0];
  }
};

const isSettingsVisit = (visit) => {
  const method = String(visit?.method || 'get').toLowerCase();

  return method === 'get'
    && !visit?.prefetch
    && /^\/shop\/[^/]+\/shop-management\/settings\/?$/.test(visitPathname(visit?.url));
};

const normalizeVisitUrl = (url) => {
  if (!url) {
    return null;
  }

  try {
    const normalized = new URL(String(url), window.location.origin);

    return normalized.origin + normalized.pathname + normalized.search;
  } catch {
    return String(url).split('#')[0];
  }
};

const startSettingsVisit = (event) => {
  const visit = event?.detail?.visit;

  // Background reloads (for example, the Time Clock broadcast refresh) do not
  // represent a navigation to Settings and cannot safely replace its visit.
  if (visit?.async || visit?.prefetch) {
    return;
  }

  if (!isSettingsVisit(visit)) {
    pendingSettingsVisit = null;
    settingsVisitError.value = null;

    return;
  }

  pendingSettingsVisit = {
    visit,
    url: normalizeVisitUrl(visit.url),
  };
  settingsVisitError.value = null;
};

const showSettingsVisitError = (event) => {
  const response = event?.detail?.response;
  const responseStatus = Number(response?.status);
  const failedUrl = normalizeVisitUrl(response?.config?.url);

  if (
    !pendingSettingsVisit
    || failedUrl !== pendingSettingsVisit.url
    || !Number.isFinite(responseStatus)
    || responseStatus < 500
  ) {
    return;
  }

  settingsVisitError.value = pendingSettingsVisit;
  event.preventDefault?.();

  return false;
};

const finishSettingsVisit = (event) => {
  if (event?.detail?.visit === pendingSettingsVisit?.visit) {
    pendingSettingsVisit = null;
  }
};

const retrySettingsVisit = () => {
  if (!settingsVisitError.value?.url) {
    return;
  }

  router.visit(settingsVisitError.value.url);
};

onMounted(() => {
  window.addEventListener('shop-management:create-estimate', createEstimate);
  removeSettingsVisitStartListener = router.on('start', startSettingsVisit);
  removeSettingsVisitInvalidListener = router.on('invalid', showSettingsVisitError);
  removeSettingsVisitFinishListener = router.on('finish', finishSettingsVisit);
});

onBeforeUnmount(() => {
  window.removeEventListener('shop-management:create-estimate', createEstimate);
  removeSettingsVisitStartListener?.();
  removeSettingsVisitInvalidListener?.();
  removeSettingsVisitFinishListener?.();
});

const openCommandPalette = () => {
  if (isAdmin.value) return;
  commandPaletteOpen.value = true;
};

const closeCommandPalette = () => {
  commandPaletteOpen.value = false;
};

const jumpToPageSection = async (section) => {
  closeCommandPalette();
  await nextTick();
  pageSectionNavigation.jump(section);
};

const closeSidebar = () => {
  if (isMobileShell.value) {
    sidebarOpen.value = false;
  }
};

const handleSidebarOpenChange = (value) => {
  sidebarOpen.value = value;
};

// Remember the desktop expand/collapse choice across reloads. Skip while in the
// mobile shell — its open state is transient (backdrop overlay) and the resize
// logic force-closes it, which would otherwise clobber the stored desktop value.
watch(sidebarOpen, (value) => {
  if (isMobileShell.value) {
    return;
  }

  try {
    window.localStorage.setItem(SIDEBAR_STATE_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // localStorage unavailable (private mode / disabled) — sidebar state just
    // won't persist; the layout still works.
  }
});

watch(() => page.component, () => {
  pageSectionNavigation.clear();
});

const setPagePrototype = (value) => {
  pagePrototypeOverride.value = Boolean(value);
};

const setPageUnfinished = (value) => {
  pageUnfinishedOverride.value = typeof value === 'boolean' ? value : null;
};

const clearPageStatus = () => {
  pagePrototypeOverride.value = null;
  pageUnfinishedOverride.value = null;
};

useShopManagementShortcuts({
  enabled: shortcutsEnabled,
  openCommandPalette,
  showSidebarBackdrop,
  sidebarOpen,
});

provide('bayhiveSkeletonLoaders', true);
provide('shopManagementScope', scopeContext);
provide(SHOP_MANAGEMENT_PAGE_SECTIONS_KEY, pageSectionNavigation);
provide('shopManagementPageStatus', {
  setPrototype: setPagePrototype,
  setUnfinished: setPageUnfinished,
  clear: clearPageStatus,
});
provide('shopManagementLayout', {
  shellWidth,
  breakpoints,
});
provide('shopManagementPageLayout', {
  contentElement: contentHost,
  setContentEndInset,
  clearContentEndInset,
});
</script>

<template>
  <AuthFlowPreview v-if="showAuthPreview" />
  <div v-else ref="shell" class="shop-management-shell" :class="{ 'shop-management-shell--admin': isAdmin }" :style="shellStyle">
    <SidebarLayout class="shop-management-shell-layout">
      <SidebarBackdrop v-if="showSidebarBackdrop" class="shop-management-shell-backdrop" />

      <AppSidebar
        :sidebar-id="sidebarId"
        :items="navItems"
        :current-page-key="currentNavKey"
        :selected-scope="scopeContext.selectedScope"
        :is-mobile-shell="isMobileShell"
        :open="sidebarOpen"
        @navigate="closeSidebar"
        @update:open="handleSidebarOpenChange"
      />

      <SidebarMain class="shop-management-shell-main">
        <UpdateAvailableBanner :visible="updateAvailable" @refresh="applyUpdate" />

        <div ref="topbarHost" class="shop-management-shell-topbar" :style="topbarStyle">
          <AppTopBar
            :sidebar-target="sidebarId"
            :layout-mode="topbarMode"
            :narrow-layout="isNarrowTopbar"
            :show-sidebar-trigger="isMobileShell"
            :selected-scope="scopeContext.selectedScope"
            :scopes="shopScopes"
            :user="user"
                  :show-estimate="showEstimate"
            :show-time-clock="showTimeClock"
            :command-palette-open="commandPaletteOpen"
            :shortcuts-enabled="shortcutsEnabled"
            @open-command-palette="openCommandPalette"
            @create-estimate="createEstimate"
            @scope-change="setScope"
          />
        </div>

        <main
          ref="contentHost"
          id="shop-management-content"
          class="shop-management-shell-content"
          :style="contentStyle"
          :aria-busy="String(contentLoading || restoringNavigation)"
        >
          <Message v-if="adminWorkspace.state.historyBlocked" severity="warn" :closable="false">
            <div class="bayhive-form-stack">
              <span
                >Your edits are still open, but the browser address has changed. Save or discard
                your changes before continuing.</span
              >
              <div class="bayhive-form-actions">
                <Button
                  label="Continue navigation"
                  size="small"
                  :loading="adminWorkspace.state.navigationBusy"
                  @click="adminWorkspace.continueHistoryNavigation"
                />
              </div>
            </div>
          </Message>
          <div
            v-if="showUnfinishedStatus"
            class="shop-management-shell-page-status"
          >
            <Tag value="Unfinished" severity="info" rounded />
          </div>

          <div v-if="settingsVisitError" class="bayhive-form-stack" aria-live="assertive">
            <Message severity="error" :closable="false">
              <div class="bayhive-form-stack">
                <strong>Settings could not be loaded.</strong>
                <span>Try the visit again. If it keeps failing, return to the dashboard and contact support.</span>
                <div class="bayhive-form-actions">
                  <Button
                    label="Try Settings Again"
                    icon="fa-solid fa-rotate-right"
                    size="small"
                    @click="retrySettingsVisit"
                  />
                </div>
              </div>
            </Message>
          </div>
          <div v-show="!settingsVisitError" class="shop-management-shell-page-stage">
            <Message v-if="isAdmin && adminWorkspace.selection.value.type === 'invalid'" severity="error" :closable="false">
              This account selection is unavailable. Choose a shop, an MSO, or All accounts.
            </Message>
            <PageLoadingBoundary
              :key="isAdmin ? adminWorkspace.contentKey.value : 'shop'"
              :page-key="pageKey"
              :visit-key="visitKey"
              @ready="presentationReady"
              class="shop-management-shell-page"
              :class="{ 'shop-management-shell-page--loading': pageContentHidden }"
              :inert="pageContentHidden || Boolean(settingsVisitError)"
              :aria-hidden="pageContentHidden || Boolean(settingsVisitError) ? 'true' : undefined"
            >
              <slot />
            </PageLoadingBoundary>
            <DestinationPageSkeleton
              v-if="showContentSkeleton"
              :destination="destinationSkeleton"
              class="shop-management-shell-navigation-skeleton"
            />
            <ContentSkeleton
              v-else-if="restoringNavigation"
              class="shop-management-shell-navigation-skeleton"
            />
          </div>
        </main>
      </SidebarMain>

      <CommandPalette
        v-if="!isAdmin"
        :show="commandPaletteOpen"
        :selected-scope="scopeContext.selectedScope"
        :show-estimate="showEstimate"
        :page-sections="pageSections"
        @close="closeCommandPalette"
        @create-estimate="createEstimate"
        @jump-page-section="jumpToPageSection"
      />
      <ConfirmDialog class="bayhive-dialog" :group="unsavedChangesConfirmationGroup" :draggable="false" />
      <ConfirmDialog
        class="bayhive-dialog"
        :group="actionConfirmationGroup"
        :draggable="false"
        :style="{ width: isAdmin ? 'min(30rem, calc(100vw - 2rem))' : 'min(42rem, calc(100vw - 2rem))' }"
      />
      <Toast />
      <Toast group="repair-order-links">
        <template #message="slotProps">
          <i
            class="fa-solid fa-calendar-check shop-management-linked-toast__icon"
            aria-hidden="true"
          ></i>
          <div class="p-toast-message-text shop-management-linked-toast__content">
            <div class="p-toast-summary">{{ slotProps.message.summary }}</div>
            <div class="p-toast-detail">
              <div>{{ slotProps.message.detail }}</div>
              <a
                v-if="slotProps.message.data?.href"
                class="shop-management-linked-toast__link"
                :href="slotProps.message.data.href"
              >
                {{ slotProps.message.data.linkLabel || 'Open repair order' }}
                <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </template>
      </Toast>
    </SidebarLayout>
    <ScrollTop
      v-if="!isAdmin"
      target="window"
      :threshold="300"
      behavior="smooth"
      icon="fa-solid fa-arrow-up"
      :buttonProps="{
        rounded: false,
        severity: 'secondary',
        variant: 'outlined',
        'aria-label': 'Back to top',
        title: 'Back to top',
      }"
    />
  </div>
</template>

<style scoped>
.shop-management-shell {
  --p-bayhive-shell-content-padding-block: var(--p-bayhive-spacing-md);
  --p-bayhive-shell-content-padding-inline: var(--p-bayhive-layout-page-gutter-compact);

  min-height: 100vh;
  background-color: light-dark(var(--p-surface-50), var(--p-surface-950));
  color: var(--p-text-color);
  isolation: isolate;
}

.shop-management-shell-layout {
  position: relative;
  min-height: 100vh;
}

.shop-management-shell--admin {
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
}
.shop-management-shell--admin .shop-management-shell-layout {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
.shop-management-shell--admin .shop-management-shell-main {
  min-height: 0;
  height: 100%;
  overflow: hidden;
}
.shop-management-shell--admin .shop-management-shell-content,
.shop-management-shell--admin .shop-management-shell-page-stage,
.shop-management-shell--admin .shop-management-shell-page {
  overflow: hidden;
}
.shop-management-shell--admin .shop-management-shell-page-stage {
  grid-template-rows: minmax(0, 1fr);
}

.shop-management-shell-backdrop {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
}

.shop-management-shell-main {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  width: 0;
  max-width: 100%;
  min-width: 0;
  min-height: 100vh;
}

.shop-management-shell-topbar {
  container: bayhive-topbar / inline-size;
  position: sticky;
  z-index: var(--p-bayhive-layout-topbar-z-index);
  top: 0;
  flex: 0 0 auto;
  min-width: 0;
  background-color: light-dark(var(--p-surface-50), var(--p-surface-950));
}

.shop-management-shell-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding-block: var(--p-bayhive-shell-content-padding-block);
  padding-inline: var(--p-bayhive-shell-content-padding-inline);
}

.shop-management-shell-page-status {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  margin-bottom: var(--p-bayhive-spacing-md);
}

.shop-management-shell-page-stage {
  position: relative;
  display: grid;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}

.shop-management-shell-page {
  display: flex;
  flex-direction: column;
  grid-area: 1 / 1;
  min-width: 0;
  min-height: 0;
}

.shop-management-shell-page--loading {
  visibility: hidden;
}

.shop-management-shell-navigation-skeleton {
  position: absolute;
  inset: 0;
  z-index: 1;
  min-width: 0;
  overflow: hidden;
  align-content: start;
  background-color: light-dark(var(--p-surface-50), var(--p-surface-950));
}

.shop-management-linked-toast__icon {
  color: var(--p-green-500);
  flex: 0 0 auto;
  margin-top: 0.125rem;
}

.shop-management-linked-toast__content {
  min-width: 0;
}

.shop-management-linked-toast__link {
  align-items: center;
  color: inherit;
  display: inline-flex;
  font-weight: 600;
  gap: var(--p-bayhive-spacing-xs);
  margin-top: var(--p-bayhive-spacing-sm);
  text-decoration: underline;
  text-underline-offset: 0.15em;
}

.shop-management-linked-toast__link:hover {
  text-decoration-thickness: 2px;
}

.shop-management-linked-toast__link:focus-visible {
  border-radius: var(--p-content-border-radius);
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

@media (min-width: 768px) {
  .shop-management-shell {
    --p-bayhive-shell-content-padding-block: var(--p-bayhive-spacing-lg);
  }
}

@media (min-width: 1024px) {
  .shop-management-shell {
    --p-bayhive-shell-content-padding-inline: var(--p-bayhive-layout-page-gutter-standard);
  }
}

@media (min-width: 1440px) {
  .shop-management-shell {
    --p-bayhive-shell-content-padding-inline: var(--p-bayhive-layout-page-gutter-wide);
  }
}
</style>
